import { Component, ElementRef, OnDestroy } from "@angular/core";
import LoggerService from "@bfx/logger";
import { ColDef, GridOptions } from "ag-grid-community";
import { GoldenLayoutPopout } from "app/golden-layout/golden-layout-popout";
import { ATDLDataModel } from "app/order-entry/data-atdl/model/atdl-data.model";
import { ATDLParameterDataModel } from "app/order-entry/data-atdl/model/atdl-parameter-data.model";
import { ICAutocompleteGenericModel } from "app/order-entry/interface/controls/models/bfx-interface-controls.models";
import { ATDLDatasetService } from "app/order-entry/services/atdl/atdl-dataset.service";
import { ATDLParserService } from "app/order-entry/services/atdl/atdl-parser.service";
import { ATDLProcessorService } from "app/order-entry/services/atdl/atdl-processor.service";
import { ATDLUtilityService } from "app/order-entry/services/atdl/atdl-utility.service";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { StrategyParameterImpl } from "shared/cubiq-model-multi-asset";
import { BfxColDef } from "shared/cubiq-services/model/TableDefinition";

import { GoldenLayoutComponent } from "../golden-layout/golden-layout-component";
import { ParameterChange } from "./bfx-atdl-designer-params.component";
import { OEInputModel } from "app/order-entry/data/models/oe-input.model";
import { ICDTransportModel } from "app/order-entry/data-idc/coordination/icd-transport.model";
import { NativeDesktopSupport } from "app/bfx-native-desktop/native-desktop-support";
const logger = LoggerService.for(__dirname);

type AlgoData = {
  atdlText: string;
  algoType: ICAutocompleteGenericModel;
  algoParams: Array<StrategyParameterImpl>;
  strategyParameters: Array<StrategyParameterImpl>;
  atdlModel: ATDLDataModel;
  error: string;
  hasValidationErrors: boolean;
  validationErrors: string[];
};

const MODEL_PREFIX = "ATDLDesigner.";

@Component({
  selector: "bfx-atdl-designer",
  templateUrl: "./html/bfx-atdl-designer.html",
  standalone: false,
})
export class BfxATDLDesignerComponent implements GoldenLayoutComponent, OnDestroy {
  private parentPopup: GoldenLayoutPopout;

  public data: AlgoData = {
    atdlText: "",
    algoType: null,
    algoParams: [],
    strategyParameters: null,
    atdlModel: null,
    error: "",
    hasValidationErrors: false,
    validationErrors: [],
  };

  public columnDefs: Array<ColDef | BfxColDef> = [];
  public gridOptions: GridOptions;

  /** Observable to indicate that values have potentially changed */
  private refreshParametersSubject: Subject<ParameterChange> = new Subject();
  /** Actual list of ATDL parameters */
  private modelParameters: Array<ATDLParameterDataModel>;

  constructor(
    private atdlUtility: ATDLUtilityService,
    private atdlParser: ATDLParserService,
    private atdlDataset: ATDLDatasetService,
    private atdlProcessor: ATDLProcessorService, // keep it here for atdl initialization
    public element: ElementRef,
  ) {
    // Tmp only
    const typeModel = new ICAutocompleteGenericModel("", "");
    this.data.algoType = typeModel;
    this.initialise();
  }
  ngOnDestroy(): void {
    this.atdlModel = null;
  }

  private initialise() {
    this.gridOptions = <GridOptions>{
      deltaRowDataMode: true,
      getRowNodeId: (data) => {
        if (data) return data["parameterName"];
        return "";
      },
      suppressContextMenu: true,
      headerHeight: 24,
      suppressColumnMoveAnimation: true,
      suppressDragLeaveHidesColumns: true,
      suppressScrollOnNewData: true,
      suppressFocusAfterRefresh: true,
      suppressAutoSize: true,
      showToolPanel: false,
      toolPanelSuppressSideButtons: true,
      enableFilter: false,
      rowDeselection: false,
      enableBrowserTooltips: true,
      onGridReady: (_params) => {
        this.gridOptions.api.setRowData(this.data.algoParams);
      },
    };
    this.columnDefs = [
      {
        headerName: "Parameter Name",
        field: "parameterName",
        editable: false,
        colId: "parameterName",
        suppressMenu: true,
        omitFromPicker: false,
        hide: false,
        minWidth: 80,
        width: 125,
      },
      {
        headerName: "Parameter Value",
        field: "parameterValue",
        editable: false,
        colId: "parameterValue",
        valueGetter: (params) => params.data.parameterValue ?? "",
        suppressMenu: true,
        cellClass: ["blotter_center_align"],
        omitFromPicker: false,
        hide: false,
        minWidth: 80,
        width: 125,
      },
      {
        headerName: "Parameter Type",
        field: "parameterType",
        editable: false,
        valueGetter: (params) =>
          params.data.parameterValue != null ? params.data.parameterType : "",
        colId: "parameterType",
        cellClass: ["blotter_center_align"],
        suppressMenu: true,
        omitFromPicker: false,
        hide: false,
        minWidth: 80,
        width: 125,
      },
      {
        headerName: "Included?",
        field: "included",
        editable: false,
        colId: "included",
        cellClass: ["blotter_center_align"],
        valueGetter: (params) => (params.data.parameterValue != null ? "Y" : "N"),
        suppressMenu: true,
        omitFromPicker: false,
        hide: false,
        minWidth: 80,
        width: 125,
      },
    ];
    NativeDesktopSupport.prepareGridOptions(this.gridOptions, this.element.nativeElement);

    // Throttle all incoming changes so should only be updating once per group of related changes
    this.refreshParametersSubject.pipe(debounceTime(100)).subscribe((parameterChange) => {
      this.refreshParameterBlotter(parameterChange);
    });
  }

  setParentPopup(parentPopup: GoldenLayoutPopout) {
    this.parentPopup = parentPopup;
    this.parentPopup.setShowDock(false);
    this.parentPopup.setShowRefresh(false);
  }

  getComponentConfiguration(): any {
    let serviceConfig: any = {};
    return serviceConfig;
  }

  restoreFromConfiguration(_config: any): void {
    //
  }

  loadATDL() {
    logger.out.warn("Parsing ATDL Text");

    this.atdlParser.parseExternalATDL(this.data.atdlText).then(
      (model: ATDLDataModel) => {
        this.atdlModel = model;
      },
      (error) => {
        this.atdlModel = null;
        this.data.error = error;
      },
    );
  }

  private set atdlModel(model: ATDLDataModel) {
    if (this.data.atdlModel) {
      this.atdlDataset.deleteInternal(this.data.atdlModel.name);
    }
    this.data.atdlModel = model;

    if (this.data.atdlModel) {
      model.id = MODEL_PREFIX + model.name;
      model.name = MODEL_PREFIX + model.name;
      this.atdlDataset.addInternal(model.name, model);
      this.atdlProcessor.processATDLModel(model);
      this.data.strategyParameters = null;
      this.modelParameters = model.parameters;
      if (this.gridOptions?.api) {
        this.gridOptions.api.setRowData(this.data.algoParams);
      }
    } else {
      this.data.algoParams = [];
      this.data.strategyParameters = null;
    }
    this.data.error = null;
  }

  /**
   * Handler when algo parameters have changed
   * @param parameterChange Object of local type ParameterChange
   */
  public parametersChanged(parameterChange): void {
    this.refreshParametersSubject.next(parameterChange);
  }

  private refreshParameterBlotter(parameterChange: ParameterChange): void {
    // if ( parameterChange.errors?.length > 0){
    //   const msgs:string[] =[];
    // parameterChange.errors.forEach( inputModel=> msgs.push( inputModel.))
    // }
    const pNames: Set<string> = new Set();
    const orderParameters = parameterChange.parameters;
    orderParameters.forEach((parameter) => pNames.add(parameter.parameterName));
    this.modelParameters.forEach((parameter) => {
      // Add parameters that will not be included in the output
      if (!pNames.has(parameter.refName)) {
        const omitted = new StrategyParameterImpl();
        omitted.parameterName = parameter.refName;
        omitted.parameterType = parameter.paramType;
        orderParameters.push(omitted);
      }
    });
    // Just rewrite the StrategyName parameter, reverting back to input name
    orderParameters.forEach((parameter) => {
      if (
        parameter.parameterName === "StrategyName" &&
        typeof parameter.parameterValue == "string" &&
        parameter.parameterValue.startsWith(MODEL_PREFIX)
      ) {
        parameter.parameterValue = parameter.parameterValue.substring(MODEL_PREFIX.length);
      }
    });

    if (parameterChange.errors?.length > 0) {
      this.data.validationErrors = this.extractErrorMessages(parameterChange.errors);
      this.data.hasValidationErrors = this.data.validationErrors.length > 0;
    } else {
      this.data.hasValidationErrors = false;
      this.data.validationErrors.length = 0;
    }
    this.data.algoParams = orderParameters;
    if (this.gridOptions.api) {
      this.gridOptions.api.setRowData(this.data.algoParams);

      this.gridOptions.api.redrawRows();
    }
  }

  private extractErrorMessages(errors: OEInputModel[]): string[] {
    return errors.map((model) => {
      if (model.value instanceof ICDTransportModel) {
        const controlId = model.value.icdId;
        const error = model.value.errorMsg;
        return `Error with control '${controlId} -  Error '${error}'`;
      }
    });
  }
}
